
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Professional, Review, ListingTier } from '../types';
import { listingService } from '../services/listingService';
import { Star, MapPin, ChevronLeft, CheckCircle, ShieldCheck, ShieldAlert, Loader2, Send, MessageSquare, Mail, Layers, ExternalLink, ArrowRight, ClipboardCheck, Building2, Linkedin, Share2, Globe, Phone } from 'lucide-react';
import SEOHandler from '../components/SEOHandler';
import { authService } from '../services/authService';

const ProfessionalProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [pro, setPro] = useState<Professional | null>(null);
  const [loading, setLoading] = useState(true);
  const user = authService.getUser();
  const isOwner = user && pro && pro.contact.email === user.email;
  
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryEmail, setEnquiryEmail] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enquirySent, setEnquirySent] = useState(false);
  const [dispatchInfo, setDispatchInfo] = useState<{to: string, time: string, message: string} | null>(null);

  useEffect(() => {
    if (id) {
      loadProfile();
    }
  }, [id]);

  const loadProfile = async () => {
    if (id) {
      const data = await listingService.getListingById(id);
      setPro(data);
      setLoading(false);
    }
  };

  const handleEnquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enquiryName || !enquiryEmail || !enquiryMessage) return;
    setIsSubmitting(true);
    try {
      const success = await listingService.sendEnquiry(pro!.contact.email, {
        name: enquiryName,
        senderEmail: enquiryEmail,
        message: enquiryMessage
      });
      if (success) {
        setDispatchInfo({ 
          to: pro!.contact.email, 
          time: new Date().toLocaleTimeString(),
          message: enquiryMessage
        });
        setEnquirySent(true);
      }
    } catch (err) {
      console.error("Enquiry failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShareLinkedIn = () => {
    if (!pro) return;
    const profileUrl = window.location.href;
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`;
    window.open(shareUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-brand-600 mb-4" size={48} />
        <p className="text-gray-400 font-black uppercase tracking-widest text-xs">Fetching Professional Data...</p>
      </div>
    );
  }

  if (!pro) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center">
        <h2 className="text-2xl font-black mb-4">Professional profile not found.</h2>
        <Link to="/directory" className="text-brand-600 font-black uppercase tracking-widest text-sm hover:underline">Return to Directory</Link>
      </div>
    );
  }

  const profileSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": pro.businessName,
    "image": pro.imageUrl,
    "logo": pro.logoUrl,
    "description": `${pro.isVerified ? '[Vetted & Verified] ' : ''}${pro.description.substring(0, 160)}`,
    "award": pro.isVerified ? "PlanStack Human-Verified Professional" : undefined,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": pro.location.suburb,
      "addressRegion": pro.location.state,
      "streetAddress": pro.location.address,
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": pro.location.lat,
      "longitude": pro.location.lng
    },
    "url": pro.contact.website || `https://planstack.com.au/#/professional/${pro.id}`,
    "telephone": pro.contact.phone,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": pro.rating,
      "reviewCount": pro.reviewCount
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <SEOHandler 
        title={`${pro.businessName} | ${pro.category} in ${pro.location.suburb}, ${pro.location.state} | PlanStack`}
        description={`Find reviews, contact details and expertise for ${pro.businessName} in ${pro.location.suburb}. ${pro.isVerified ? 'Vetted and verified professional.' : ''} Specialising in ${pro.category} for Australian developments.`}
        schema={profileSchema}
      />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center text-[10px] uppercase tracking-widest font-black text-gray-400 space-x-2">
            <Link to="/" className="hover:text-brand-600">Home</Link>
            <span>/</span>
            <Link to="/directory" className="hover:text-brand-600">Directory</Link>
            <span>/</span>
            <span className="text-gray-900 truncate">{pro.businessName}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/directory" className="inline-flex items-center text-xs font-black uppercase tracking-widest text-brand-600 hover:text-brand-800 mb-6">
          <ChevronLeft size={16} className="mr-1" /> Back to Search
        </Link>

        {isOwner && pro.isVerified && (
          <div className="mb-8 bg-blue-600 rounded-3xl p-6 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 animate-fade-in text-white border-4 border-white">
             <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-sm">
                   <ShieldCheck size={32} />
                </div>
                <div>
                   <h3 className="text-lg font-black uppercase tracking-tight">Your Profile is Verified!</h3>
                   <p className="text-sm font-medium opacity-90">Share your verified status on LinkedIn to boost your authority.</p>
                </div>
             </div>
             <button onClick={handleShareLinkedIn} className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:bg-blue-50 transition flex items-center gap-3 active:scale-95 shrink-0">
                <Linkedin size={18} /> Share My Profile
             </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
               <div className="h-64 sm:h-96 relative">
                 <img src={pro.imageUrl || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1200'} className="w-full h-full object-cover" alt={`${pro.businessName} office and project work`} />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end p-10">
                    <div className="flex items-center gap-6">
                      {pro.logoUrl && (
                        <div className="w-24 h-24 bg-white p-2 rounded-2xl shadow-2xl flex-shrink-0">
                           <img src={pro.logoUrl} className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div className="text-white">
                         <div className="flex items-center gap-2 mb-2">
                           {pro.isVerified && (
                             <span className="bg-green-500 text-white text-[9px] px-3 py-1 rounded-full font-black uppercase tracking-wider flex items-center gap-1 shadow-lg">
                               <ShieldCheck size={10} /> Verified
                             </span>
                           )}
                           <span className="bg-brand-500 text-white text-[9px] px-3 py-1 rounded-full font-black uppercase tracking-wider shadow-lg">{pro.tier}</span>
                         </div>
                         <h1 className="text-3xl sm:text-5xl font-black mb-1 leading-none">{pro.businessName}</h1>
                         <p className="text-brand-200 font-bold text-lg">{pro.category}</p>
                      </div>
                    </div>
                 </div>
               </div>
               <div className="p-10">
                 <div className="flex flex-wrap items-center gap-8 mb-10 border-b border-gray-100 pb-10">
                    <div className="flex items-center gap-2">
                       <Star size={20} className="text-yellow-400 fill-current" />
                       <span className="text-xl font-black text-gray-900">{pro.rating}</span>
                       <span className="text-gray-400 text-sm">({pro.reviewCount} Reviews)</span>
                    </div>
                    <div className="flex items-center text-gray-600 font-bold uppercase tracking-tighter text-sm">
                       <MapPin size={18} className="mr-2 text-brand-600" />
                       {pro.location.suburb}, {pro.location.state}
                    </div>
                    {pro.contact.website && (
                      <a href={pro.contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-600 font-black text-[10px] uppercase tracking-widest hover:underline">
                        <Globe size={16} /> Visit Website
                      </a>
                    )}
                 </div>
                 <div className="prose max-w-none text-gray-700 leading-relaxed mb-10">
                   <h2 className="text-2xl font-black text-gray-900 mb-6 uppercase tracking-tight">Practice Expertise</h2>
                   <p className="text-lg whitespace-pre-wrap">{pro.description}</p>
                 </div>

                 {pro.gallery && pro.gallery.length > 0 && (
                   <div className="mt-16 pt-10 border-t border-gray-100">
                      <h2 className="text-2xl font-black text-gray-900 mb-10 uppercase tracking-tight">Project Showcase</h2>
                      <div className="space-y-12">
                        {pro.gallery.map((item, idx) => (
                          <div key={idx} className="flex flex-col md:flex-row gap-8 items-start">
                            <div className="w-full md:w-1/2 aspect-video rounded-3xl overflow-hidden shadow-lg bg-gray-100">
                               <img src={item.url} className="w-full h-full object-cover" alt={`Project ${idx + 1}`} />
                            </div>
                            <div className="flex-1">
                               <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 text-brand-600 text-[9px] font-black uppercase tracking-widest mb-4">Project Case Study</div>
                               <p className="text-gray-600 text-lg font-medium leading-relaxed italic">"{item.description}"</p>
                            </div>
                          </div>
                        ))}
                      </div>
                   </div>
                 )}
               </div>
            </section>
          </div>
          <aside className="space-y-6">
             <div className="bg-white rounded-3xl shadow-2xl border border-brand-100 p-8 sticky top-24">
                <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight leading-none mb-6">Contact {pro.businessName}</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <Phone className="text-brand-600" size={18} />
                    <span className="font-black text-gray-900">{pro.contact.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <Mail className="text-brand-600" size={18} />
                    <span className="font-black text-gray-900 text-xs truncate">{pro.contact.email}</span>
                  </div>
                </div>
                {enquirySent ? (
                  <div className="text-center py-6">
                    <CheckCircle size={40} className="text-green-600 mx-auto mb-4" />
                    <p className="font-bold text-gray-900 text-sm">Enquiry Dispatched</p>
                  </div>
                ) : (
                  <form onSubmit={handleEnquiry} className="space-y-4">
                     <input required value={enquiryName} onChange={e => setEnquiryName(e.target.value)} className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-500 font-medium text-sm transition shadow-inner" placeholder="Your Name" />
                     <input required type="email" value={enquiryEmail} onChange={e => setEnquiryEmail(e.target.value)} className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-500 font-medium text-sm transition shadow-inner" placeholder="Email Address" />
                     <textarea required value={enquiryMessage} onChange={e => setEnquiryMessage(e.target.value)} className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-500 font-medium text-sm transition shadow-inner" rows={6} placeholder="How can we help?" />
                     <button type="submit" disabled={isSubmitting} className="w-full bg-brand-900 hover:bg-black text-white font-black py-4 rounded-xl transition shadow-xl uppercase tracking-widest text-[10px] flex items-center justify-center gap-2">
                        {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        Send Enquiry
                     </button>
                  </form>
                )}
             </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfilePage;
